import type { CreditCardDetails, TicketPurchaseDto } from "@public/models.g";

// Subset of a coalesce-vue ApiCaller: callable, plus the state fields the caller
// updates after settling. `await caller(args)` returns the unwrapped TResult; the
// envelope fields (wasSuccessful, message) live as state on the caller object itself.
type Caller<TArgs extends unknown[], TResult> = {
  (...args: TArgs): Promise<TResult | undefined | void>;
  wasSuccessful: boolean | null;
  result: TResult | null;
  message?: string | null;
};

export type SeriesPurchaseService = {
  initiateSeriesPurchase: Caller<
    [number | null, (string | null | undefined)?],
    TicketPurchaseDto
  >;
  setSeriesPassTypeQuantity: Caller<
    [string | null, (number | null | undefined)?, (number | null | undefined)?],
    TicketPurchaseDto
  >;
  completeSeriesPayment: Caller<
    [string | null, (CreditCardDetails | null | undefined)?],
    string
  >;
};

export type InitiateOutcome =
  | { ok: true; purchase: TicketPurchaseDto }
  | { ok: false; message: string; reason: "access-code" | "error" };

export type SetQuantityOutcome =
  | { ok: true; purchase: TicketPurchaseDto }
  | { ok: false; message: string };

export type CompleteOutcome =
  | { ok: true; redirectUrl: string | null }
  | { ok: false; message: string };

const INITIATE_FALLBACK = "This event series is not available.";
// Coalesce exposes only a message, so detect the access-code case once here; consumers
// branch on the typed reason instead of re-matching the text.
const ACCESS_CODE_HINT = /access code/i;
const SET_QUANTITY_FALLBACK = "Could not update pass quantity.";
const COMPLETE_FALLBACK = "Payment failed.";

/**
 * Adapts the three series-purchase Coalesce callers into discriminated outcomes.
 * Centralizes the caller-state read so wire-shape regressions show up in unit tests
 * rather than as opaque runtime errors on the public purchase page.
 */
export function useSeriesPurchaseOrchestrator(service: SeriesPurchaseService) {
  async function initiate(
    eventSeriesId: number,
    accessCode?: string,
  ): Promise<InitiateOutcome> {
    const c = service.initiateSeriesPurchase;
    // A failed ItemResult (e.g. access code required) comes back as an HTTP error that rejects
    // the call, but coalesce-vue still populates the caller state we read below.
    try {
      await c(eventSeriesId, accessCode ?? null);
    } catch {
      /* fall through to the caller-state read */
    }
    if (c.wasSuccessful && c.result) {
      return { ok: true, purchase: c.result };
    }
    const message = c.message ?? INITIATE_FALLBACK;
    return {
      ok: false,
      message,
      reason: ACCESS_CODE_HINT.test(message) ? "access-code" : "error",
    };
  }

  async function setPassQuantity(
    publicId: string,
    passTypeId: number,
    quantity: number,
  ): Promise<SetQuantityOutcome> {
    const c = service.setSeriesPassTypeQuantity;
    try {
      await c(publicId, passTypeId, quantity);
    } catch {
      /* a rejected HTTP error still sets caller state; read it below */
    }
    if (c.wasSuccessful && c.result) {
      return { ok: true, purchase: c.result };
    }
    return { ok: false, message: c.message ?? SET_QUANTITY_FALLBACK };
  }

  async function complete(
    publicId: string,
    cardDetails: CreditCardDetails | null,
  ): Promise<CompleteOutcome> {
    const c = service.completeSeriesPayment;
    try {
      await c(publicId, cardDetails);
    } catch {
      /* a rejected HTTP error still sets caller state; read it below */
    }
    if (!c.wasSuccessful) {
      return { ok: false, message: c.message ?? COMPLETE_FALLBACK };
    }
    return { ok: true, redirectUrl: c.result ?? null };
  }

  return { initiate, setPassQuantity, complete };
}
